import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import Modal from 'components/ui/Modal';
import { CancelButton, ConfirmButton } from 'components/ui/Button';
import { historyShape } from 'constants/propTypes';

import { GET_BOOKING } from 'store/queries';
import { CANCEL_BOOKING } from 'store/mutations';
import { getClubEurostarSubscriptionURL, getRefundMethodsURL } from 'utils/urls';
import { useManagedMutation } from 'utils/managedHooks';
import { useLazyQuery } from '@apollo/client';

import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import { StyledCancelBeforePaymentButtons } from '../SubscriptionDetails.styles';

const CancelBeforePayment = ({ onClose, bookingReference, currency, membershipNumber, history }) => {
  const [cancelBooking, { loading: cancelBookingLoading, error: cancelBookingError }] = useManagedMutation(CANCEL_BOOKING);
  const [getBooking, { loading: bookingLoading }] = useLazyQuery(GET_BOOKING, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => handleRedirect(data)
  });

  const handleRedirect = (data) => {
    onClose();
    return history.push(
      data?.booking?.paymentsRefundBalance < 0
        ? getRefundMethodsURL(bookingReference, currency, membershipNumber, false, true)
        : getClubEurostarSubscriptionURL(membershipNumber),
      {
        shouldRetry: true
      }
    );
  };

  const handleOnSubmit = async () => {
    try {
      await cancelBooking({
        variables: {
          bookingReference
        }
      });

      await getBooking({
        variables: {
          reference: bookingReference
        }
      });
    } catch (error) {
      throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
        errorCTARedirect: getClubEurostarSubscriptionURL(membershipNumber)
      });
    }
  };

  if (cancelBookingError) {
    throw new AgentPortalError(cancelBookingError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: getClubEurostarSubscriptionURL(membershipNumber)
    });
  }

  const loading = cancelBookingLoading || bookingLoading;

  return (
    <Modal isOpen={true} title={<FormattedMessage id="afterSales.cancel" />} onClose={onClose}>
      <FormattedMessage id="review.removeModal.subtitle" />

      <StyledCancelBeforePaymentButtons>
        <ConfirmButton
          data-testid="manageBooking.confirmCancellation"
          data-cy="manageBooking.confirmCancellation"
          onClick={handleOnSubmit}
          disabled={loading}
          loading={loading}
        >
          <FormattedMessage id="manageBooking.confirmCancellation" />
        </ConfirmButton>
        <CancelButton onClick={onClose} data-testid="manageBooking.abandonCancellation">
          <FormattedMessage id="manageBooking.abandonCancellation" />
        </CancelButton>
      </StyledCancelBeforePaymentButtons>
    </Modal>
  );
};

CancelBeforePayment.propTypes = {
  onClose: PropTypes.func,
  bookingReference: PropTypes.string,
  currency: PropTypes.string,
  membershipNumber: PropTypes.string,
  history: historyShape
};

export default CancelBeforePayment;
